import React from 'react';
import Icon from '../../../components/AppIcon';

const BikeTypeSelector = ({ 
  selectedType, 
  onTypeChange, 
  disabled = false 
}) => {
  const bikeTypes = [
    {
      id: 'road',
      name: 'Road Bike',
      description: 'Drop handlebars, thin tires, lightweight frame',
      icon: 'Bike',
      features: ['Aerodynamic position', 'Speed focused', 'Smooth surfaces']
    },
    {
      id: 'mountain',
      name: 'Mountain Bike',
      description: 'Wide tires, suspension, rugged build',
      icon: 'Mountain',
      features: ['Off-road capability', 'Suspension system', 'Durable components']
    },
    {
      id: 'hybrid',
      name: 'Hybrid Bike',
      description: 'Flat handlebars, medium tires, versatile',
      icon: 'MapPin',
      features: ['Comfortable riding', 'City & trail ready', 'Upright position']
    },
    {
      id: 'electric',
      name: 'Electric Bike',
      description: 'Motor assistance, battery powered',
      icon: 'Zap',
      features: ['Motor assistance', 'Extended range', 'Easy climbing']
    },
    {
      id: 'gravel',
      name: 'Gravel Bike',
      description: 'Drop bars, wide tires, adventure ready',
      icon: 'Compass',
      features: ['Mixed terrain', 'Adventure touring', 'Versatile gearing']
    },
    {
      id: 'other',
      name: 'Other',
      description: 'BMX, cruiser, or specialty bike',
      icon: 'HelpCircle',
      features: ['Specialty design', 'Unique components', 'Custom analysis']
    }
  ];

  const handleTypeSelect = (typeId) => {
    if (!disabled && onTypeChange) {
      onTypeChange(typeId);
    }
  };

  return (
    <div className="w-full">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-foreground mb-2">
          What type of bike are you uploading?
        </h3>
        <p className="text-sm text-muted-foreground">
          Selecting your bike type helps our AI provide more accurate component recognition and upgrade recommendations.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {bikeTypes?.map((type) => (
          <button
            key={type?.id}
            onClick={() => handleTypeSelect(type?.id)}
            disabled={disabled}
            className={`p-4 rounded-lg border-2 text-left transition-all duration-200 interactive-scale ${
              selectedType === type?.id
                ? 'border-accent bg-accent/5 shadow-md'
                : disabled
                ? 'border-border bg-muted/50 cursor-not-allowed opacity-60' :'border-border hover:border-accent/50 hover:bg-accent/5'
            }`}
          >
            <div className="flex items-start space-x-3">
              <div className={`p-2 rounded-full flex-shrink-0 ${
                selectedType === type?.id
                  ? 'bg-accent text-accent-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}>
                <Icon name={type?.icon} size={20} />
              </div>
              
              <div className="flex-1 min-w-0">
                <h4 className={`font-medium mb-1 ${
                  selectedType === type?.id ? 'text-accent' : 'text-foreground'
                }`}>
                  {type?.name}
                </h4>
                <p className="text-sm text-muted-foreground mb-2">
                  {type?.description}
                </p>
                
                {/* Features */}
                <div className="space-y-1">
                  {type?.features?.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-1">
                      <div className={`w-1 h-1 rounded-full ${
                        selectedType === type?.id ? 'bg-accent' : 'bg-muted-foreground'
                      }`} />
                      <span className="text-xs text-muted-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Selection Indicator */}
              {selectedType === type?.id && (
                <div className="flex-shrink-0">
                  <Icon name="CheckCircle" size={20} color="var(--color-accent)" />
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
      {/* Additional Info */}
      {selectedType && (
        <div className="mt-4 p-3 bg-accent/5 border border-accent/20 rounded-lg">
          <div className="flex items-start space-x-2">
            <Icon name="Info" size={16} color="var(--color-accent)" className="mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="text-accent font-medium mb-1">
                Great choice! {bikeTypes?.find(t => t?.id === selectedType)?.name} selected.
              </p>
              <p className="text-muted-foreground">
                Our AI will focus on components and upgrades specific to your bike type for more accurate recommendations.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BikeTypeSelector;